/**
 * Chat Component
 * 
 * Provides interactive interface for chatting with an AI assistant with multi-line input support.
 * Messages are displayed in chronological order for clean conversation flow.
 * 
 * Chat Integration:
 * - Optional onOpenFullPage prop provides quick navigation to the dedicated chat page
 * - Optional showSessionSidebar prop toggles the embedded session list for lightweight layouts
 * - Expand button visible only when onOpenFullPage prop is provided (right-panel context)
 * - Button icon remains a single expand indicator since sidebar-only collapse is removed
 * - Full-page experience handled via /chat route instead of sidebar expansion
 * 
 * Enhanced Animations:
 * - Smooth slide-in animation from right when chat panel opens
 * - Sophisticated expand/collapse transitions with custom easing
 * - Interactive button animations with hover effects and micro-interactions
 * - Hardware-accelerated transforms for optimal performance
 * - Custom CSS animations for smooth state transitions
 * 
 * SSE Event Integration:
 * - Real-time progress messages for server-owned tool calls and warnings
 * - UI-only messages excluded from server conversation while displayed chronologically
 * - **Immediate visual feedback**: Tool progress appears as soon as SSE events are received
 * - **Loading continuity**: Assistant loading dots remain visible until text starts streaming or the final message renders
 * - **Real-time message updates**: Messages refreshed during streaming to capture SSE events immediately
 * - **Persistent display**: Progress hints remain visible throughout activity (no auto-dismiss)
 * - **Real-time updates**: Event messages updated in-place as new status data arrives
 * - Color-coded progress indicators for server-side activity
 * - Proper message filtering ensures only conversation messages sent to LLM
 * 
 * Multi-Line Input Support:
 * - Textarea-based input with auto-resize functionality
 * - Shift+Enter: adds a new line for multi-line messages
 * - Enter: submits the message
 * - Auto-resize based on content with configurable min/max rows
 * - Consistent styling with existing theme classes
 * 
 * Voice Input Support:
 * - Web Speech API integration provides hands-free message input
 * - Microphone button positioned to the left of the send button for easy access
 * - Visual feedback with red recording state and animated pulse during active listening
 * - Automatic transcription populates the input field with recognized speech
 * - Browser compatibility detection with graceful degradation for unsupported browsers
 * - Proper error handling and state management for speech recognition events
 * 
 * Event Bus Integration:
 * - Listens for 'chat:open' events from other components (Notes, Accounts, Contacts, etc.)
 * - Automatically clears chat history when opened via event bus for fresh context
 * - Preserves existing chat when manually accessing the page
 * 
 * Message Display:
 * - Chronological message display with selective message type visibility
 * - User messages: Standard chat bubbles (right-aligned) with edit and save functionality
 *   * Edit button appears on hover to the left of user messages  
 *   * Save button appears on hover to save message as a prompt template (auto-generates title from content)
 *   * Editing shows textarea with Update/Cancel buttons
 *   * Upon update, removes all subsequent messages and resubmits conversation to LLM
 * - Assistant messages: Content with markdown support and user choice buttons (left-aligned)
 * - Server tool progress: Displayed through SSE progress messages
 * - Real-time message streaming with markdown support and JSON-aware rendering
 *   * During streaming: JSON content is processed smartly to prevent black block display
 *   * Incomplete JSON blocks are automatically removed during streaming
 *   * Complete action_options JSON are rendered as interactive choice buttons immediately
 *   * Regular JSON blocks are hidden during streaming and shown after completion
 *   * After streaming: JSON content is processed and rendered appropriately
 *   * Action options (action_options type JSON) are rendered as interactive choice buttons
 * - Message persistence across sessions
 * - Automatic scrolling to bottom after user interactions
 * 
 * Key features:
 * - Real-time message streaming with markdown support and JSON-aware rendering
 * - Server tool progress with detailed arguments and results available on expansion
 * - Message persistence with the current new-chat user/assistant protocol
 * - Event bus integration for external communication with automatic history clearing
 * - Automatic scrolling to bottom after user interactions
 * - Voice input support with Web Speech API integration
 * - Streaming-aware JSON rendering: Prevents black block display by removing incomplete JSON during streaming, shows completed JSON with action options
 * - User message editing: Edit any user message, automatically truncates subsequent history, and resubmits conversation to LLM
 * - Message copying: Copy all conversation messages to clipboard in formatted text with timestamps
 * - Prompt library integration: Save user messages as reusable prompt templates with one click (auto-generates title from content)
 * 
 * Voice Input Features:
 * - Microphone button with visual feedback (red when recording)
 * - Web Speech API integration for speech-to-text conversion
 * - Automatic text input population from voice recognition
 * - Browser compatibility detection for speech recognition
 * - Visual indicators for listening state with proper accessibility labels
 * 
 * Scrolling Behavior:
 * - Automatic scroll to bottom occurs immediately after user submits a message
 * - Uses setTimeout(scrollToBottom, 0) to ensure DOM updates are processed before scrolling
 * - Additional scrolling happens when streaming content updates or messages change
 * - Smooth scrolling behavior provides better user experience
 * - Scroll triggers work for all message submission methods (manual input, choice selection, external events)
 * - Reliable scrolling timing prevents race conditions with DOM updates
 * 
 * Message Flow:
 * 1. User sends message via input form
 * 2. chatService.sendMessage starts and adds user message synchronously to its storage
 * 3. UI immediately updates to show user message by getting latest messages from chatService
 * 4. Screen automatically scrolls to bottom to show user's new message
 * 5. Message streamed to the server-owned new-chat backend
 * 6. Server tool progress is displayed from tool.call/tool.result events
 * 7. Results consolidated and persisted to chat history by chatService
 * 8. Final scroll to bottom after response completion
 * - User messages appear instantly in UI without duplicates by leveraging chatService storage
 * - Server tool progress is visible without client-side tool execution
 * 
 * Utility Integration:
 * - Message processing utilities for filtering, sorting, and formatting
 * - Speech recognition utilities for voice input setup and management
 * - Error handling utilities for standardized error processing and categorization
 * - Request management utilities for deduplication and loading state management
 * - Message validation utilities for input validation and sanitization
 * 
 * Enhanced Error Logging:
 * - Comprehensive error details with stack traces and error types for debugging
 * - Chat service state inspection including message count and last message analysis
 * - Request state tracking with loading status, progress indicators, and streaming content
 * - Message content and request context for error reproduction
 * - Streaming event context for troubleshooting
 * - Differentiated error sources (handleSendMessage vs handleSubmitFromChoice vs handleEditMessage)
 * 
 * Changes:
 * - July 31, 2026 - Renders UI-only frontend draft handoffs separately from server tool progress.
 * - July 30, 2026 - Commits edited branch removal before generation starts and validates contextual HITL free text locally.
 * - July 29, 2026 - Restricts editing to durable user turns and hides it after failed history reconciliation.
 * - July 28, 2026 - Keeps the composer enabled for unambiguous one-question HITL prompts.
 * - July 28, 2026 - Replaces Send with request-scoped Stop while assistant generation is active.
 * - July 28, 2026 - Aligns pending composer answers and cancellation with canonical llm-runtime 0.7 outcomes.
 * - May 22, 2026 - Added responsive starter cards and quick suggestion chips for empty chat sessions.
 * - May 22, 2026 - Added llm-runtime ask_user_input prompt rendering and durable tool-response submission.
 * - May 22, 2026 - Retired legacy client-side function execution; chat now relies on server-owned new-chat tools.
 * - Extracted non-UI functions to chat utilities for better separation of concerns
 * - Implemented utility-based message processing, error handling, and validation
 * - Enhanced speech recognition setup using reusable utility functions
 * - Standardized error message processing and session expiry detection
 * - Improved code maintainability and testability through utility extraction
 * - Added drag and drop support for note cards with AI summarization
 * - Drop handler uses existing CHAT_OPEN event for seamless integration
 * - Note summarization leverages existing chat service and event bus infrastructure
 * - Uses getNotesSummaryPrompt from summaryUtils (same as AccountNotes) for consistency
 * - **MAJOR: SSE Event Message History Integration**
 *   * Extended ChatMessage model to support UI-only messages with isUIOnly flag
 *   * Added SSE event message types for real-time server progress
 *   * Modified chatService to create UI-only messages from new-chat tool.call/tool.result events
 *   * Updated getRecentHistory() to exclude UI-only messages from server communication
 *   * Added SSEProgressMessage component for immediate visual feedback
 *   * Shows server progress indicators without suppressing the assistant loading state
 *   * Implemented persistent display for ongoing activity awareness (no auto-dismiss)
 *   * Added real-time message updates with fresh timestamps for immediate UI refresh
 *   * Ensured chronological message ordering with proper filtering for server requests
 * 
 * Updated: Tool response messages are now hidden from UI display
 * Updated: Server-side tool progress now renders through SSE progress messages
 * Updated: Execution source tracking integrated into ChatMessage model and UI components
 * Updated: Extracted non-UI functions to chat utilities in /src/utils/chat/ for better code organization
 * Updated: Fixed "New Chat" button to properly create new session via handleNewSession() instead of just clearing local state
 * Updated: Removed unused handleNewChat() function that only cleared local state without creating a session
 * Updated: New chat sessions now use title format: chat-{mm-dd-yy hh:mm:ss} for better timestamp visibility
 * Updated: Drop zone integration uses existing CHAT_OPEN event for note summarization requests
 * Updated: Note formatting uses summaryUtils.getNotesSummaryPrompt for consistent experience with AccountNotes
 * Updated: SSE events now create immediate progress messages while the loading state remains visible
 * Updated: SSE progress messages render server-owned tool progress directly
 * Updated: Loading dots remain visible while waiting for assistant text, even when tool progress is displayed
 * Updated: June 14, 2025 - Enhanced error logging with comprehensive debugging information for troubleshooting SSE event parsing and message handling issues
 */

import { useState, useEffect, useRef, useCallback, FormEvent, type ComponentType, type SVGProps } from 'react';
import { flushSync } from 'react-dom';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CursorArrowRaysIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { chatService, type ChatRequestHandle } from '../services/chatService';
import { useEventBus } from '../context/EventBusContext';
import {
  ChatMessage,
  HUMAN_INPUT_FREEFORM_MAX_LENGTH,
  HumanInputAnswer,
  HumanInputRequest
} from '../models/ChatMessage';
import { AssistantMessage, UserMessage, SSEProgressMessage, HumanInputMessage, ClientActionMessage } from '../components/chat';
import ChatSidebar from '../components/ChatSidebar';
import { chatSessionService } from '../services/chatSessionService';
import { promptService } from '../services/promptService';
import { EVENTS, type NoteDragToChatEventData, type ChatOpenEventData } from '../constants/eventConstants';
import { BREAKPOINTS } from '../constants/layoutConstants';
import { useMultiLineInput } from '../hooks/useMultiLineInput';
import { getNotesSummaryPrompt } from '../utils/summaryUtils';
import {
  displayMessages,
  sortMessagesByTimestamp,
  formatMessagesForClipboard,
  getCopiableMessageCount,
  createSpeechRecognition,
  isSpeechRecognitionSupported,
  cleanupSpeechRecognition,
  getErrorMessage,
  isSessionExpiredError,
  validateFormInput
} from '../utils/chat';
import { getComposerHumanInputQuestion, normalizeHumanInputFreeform } from '../utils/humanInputContract';


interface ChatProps {
  onClose?: () => void;
  onOpenFullPage?: () => void;
  showSessionSidebar?: boolean;
  isFullPage?: boolean;
  initialSessionId?: number;
  onSessionChange?: (sessionId: number, options?: { replace?: boolean }) => void;
}

type StarterTone = 'risk' | 'opportunity' | 'relationship' | 'task';

interface StarterPrompt {
  label: string;
  compactLabel?: string;
  prompt: string;
}

interface StarterCategory {
  title: string;
  tone: StarterTone;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  prompts: StarterPrompt[];
}

interface QuickSuggestion {
  label: string;
  prompt: string;
  tone: StarterTone;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const CHAT_SIDEBAR_COLLAPSED_STORAGE_KEY = 'chat-sidebar-collapsed';

const starterToneClasses: Record<StarterTone, {
  iconWrap: string;
  icon: string;
  chip: string;
}> = {
  risk: {
    iconWrap: 'bg-red-50 dark:bg-red-950/50',
    icon: 'text-red-600 dark:text-red-300',
    chip: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900 dark:hover:bg-red-950'
  },
  opportunity: {
    iconWrap: 'bg-emerald-50 dark:bg-emerald-950/50',
    icon: 'text-emerald-600 dark:text-emerald-300',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900 dark:hover:bg-emerald-950'
  },
  relationship: {
    iconWrap: 'bg-violet-50 dark:bg-violet-950/50',
    icon: 'text-violet-600 dark:text-violet-300',
    chip: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900 dark:hover:bg-violet-950'
  },
  task: {
    iconWrap: 'bg-blue-50 dark:bg-blue-950/50',
    icon: 'text-blue-600 dark:text-blue-300',
    chip: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-blue-950'
  }
};

const starterCategories: StarterCategory[] = [
  {
    title: 'Renewals at Risk',
    tone: 'risk',
    Icon: ShieldExclamationIcon,
    prompts: [
      {
        label: 'Which brokerages have renewal risk right now?',
        compactLabel: 'Which accounts have renewals in the next 30 days?',
        prompt: 'Which brokerages have renewal risk right now?'
      },
      {
        label: 'Which renewals have no recent owner contact?',
        compactLabel: 'Show contracts at risk of not renewing',
        prompt: 'Which renewals have no recent owner contact?'
      },
      {
        label: 'What should I do next for my highest-risk renewals?',
        compactLabel: 'Which accounts have declined engagement?',
        prompt: 'What should I do next for my highest-risk renewals?'
      }
    ]
  },
  {
    title: 'Prospecting Opportunities',
    tone: 'opportunity',
    Icon: CursorArrowRaysIcon,
    prompts: [
      {
        label: 'Which prospects should I focus on this week?',
        compactLabel: 'Which prospects have high engagement?',
        prompt: 'Which prospects should I focus on this week?'
      },
      {
        label: 'Which warm prospects have gone stale?',
        compactLabel: 'Show accounts that match our ICP',
        prompt: 'Which warm prospects have gone stale?'
      },
      {
        label: 'What is the best angle to approach this brokerage?',
        compactLabel: 'Find new prospects in my target market',
        prompt: 'What is the best angle to approach this brokerage?'
      }
    ]
  },
  {
    title: 'Relationships Needing Attention',
    tone: 'relationship',
    Icon: UserGroupIcon,
    prompts: [
      {
        label: 'Which important brokerages are not being actively managed?',
        compactLabel: "Who haven't I contacted recently?",
        prompt: 'Which important brokerages are not being actively managed?'
      },
      {
        label: 'What did we promise but not follow up on?',
        compactLabel: 'Show key accounts with no recent activity',
        prompt: 'What did we promise but not follow up on?'
      },
      {
        label: 'Which accounts have no next step?',
        compactLabel: 'Which relationships are losing engagement?',
        prompt: 'Which accounts have no next step?'
      }
    ]
  }
];

const quickSuggestions: QuickSuggestion[] = [
  {
    label: 'Find renewals at risk',
    prompt: 'Find renewals at risk',
    tone: 'risk',
    Icon: ShieldExclamationIcon
  },
  {
    label: 'Find prospecting opportunities',
    prompt: 'Find prospecting opportunities',
    tone: 'opportunity',
    Icon: CursorArrowRaysIcon
  },
  {
    label: 'Find stale relationships',
    prompt: 'Find stale relationships',
    tone: 'relationship',
    Icon: UserGroupIcon
  },
  {
    label: 'Create tasks',
    prompt: 'Create tasks from the highest-priority follow-ups',
    tone: 'task',
    Icon: CheckCircleIcon
  }
];

const buildNewSessionTitle = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `chat-${mm}-${dd}-${yy} ${hh}:${min}:${ss}`;
};

const getChatMessagesSnapshot = () => [...chatService.getMessages()];

const isUnavailableChatSessionError = (error: unknown, sessionId: number) => {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message === `Session ${sessionId} not found`
    || /\bstatus (403|404)\b/.test(error.message);
};

const Chat = ({
  onClose,
  onOpenFullPage,
  showSessionSidebar = true,
  isFullPage = false,
  initialSessionId,
  onSessionChange
}: ChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditPersisting, setIsEditPersisting] = useState(false);
  const [activeChatRequest, setActiveChatRequest] = useState<ChatRequestHandle | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return window.matchMedia(BREAKPOINTS.desktop).matches;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    const storedValue = window.localStorage.getItem(CHAT_SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (storedValue === 'true') {
      return true;
    }
    if (storedValue === 'false') {
      return false;
    }

    return true;
  });
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [sessionRefreshToken, setSessionRefreshToken] = useState(0);
  const [isServiceInitialized, setIsServiceInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializationAttemptedRef = useRef(false);
  const initialSessionAttemptRef = useRef<number | null>(null);
  const eventBus = useEventBus();
  const { publish } = eventBus;

  // Add ref to prevent duplicate requests from React StrictMode
  const requestInProgressRef = useRef(false);
  const registerChatRequest = useCallback((request: ChatRequestHandle) => {
    setActiveChatRequest(request);
  }, []);
  const clearChatRequest = useCallback((request: ChatRequestHandle) => {
    setActiveChatRequest((activeRequest) =>
      activeRequest === request ? null : activeRequest
    );
  }, []);
  const handleStopGenerating = useCallback((request: ChatRequestHandle) => {
    return request.stop();
  }, []);
  const triggerSessionRefresh = useCallback(() => {
    setSessionRefreshToken(prev => prev + 1);
  }, []);

  const createAndActivateSession = useCallback(async () => {
    const title = buildNewSessionTitle();
    const newSession = await chatSessionService.createSession(title, true);
    await chatService.switchSession(newSession.id);
    setMessages([]);
    setCurrentSessionId(newSession.id);
    setStreamingContent('');
    setError(null);
    triggerSessionRefresh();
    onSessionChange?.(newSession.id);
    return newSession.id;
  }, [triggerSessionRefresh, onSessionChange]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        activeChatRequest &&
        handleStopGenerating(activeChatRequest)
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [activeChatRequest, handleStopGenerating]);

  const handleSidebarCollapsedChange = useCallback((collapsed: boolean, options?: { persist?: boolean }) => {
    setSidebarCollapsed(collapsed);

    if (options?.persist === false || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(CHAT_SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  }, []);

  const answeredHumanInputToolCallIds = new Set(
    messages
      .filter((message) => message.role === 'tool' && message.name === 'ask_user_input')
      .map((message) => message.tool_call_id)
      .filter((toolCallId): toolCallId is string => Boolean(toolCallId))
  );
  const pendingHumanInputMessage = messages.reduce<ChatMessage | null>((pending, message) => {
    const toolCallId = message.eventData?.details?.pendingHumanInput?.toolCallId;
    if (
      message.isUIOnly &&
      message.sseEventType === 'human_input' &&
      message.eventData?.status === 'pending' &&
      toolCallId &&
      !answeredHumanInputToolCallIds.has(toolCallId)
    ) {
      return message;
    }

    return pending;
  }, null);
  const hasPendingHumanInput = pendingHumanInputMessage !== null;
  const historyReconciliationRequired = chatService.isHistoryReconciliationRequired();
  const pendingHumanInputRequest = pendingHumanInputMessage?.eventData?.details?.request as
    | HumanInputRequest
    | undefined;
  const composerHumanInputQuestion = getComposerHumanInputQuestion(pendingHumanInputRequest);
  const canAnswerPendingHumanInputWithComposer = composerHumanInputQuestion !== null;

  // Multi-line input hook for handling shift+enter functionality
  const { textareaRef, handleKeyDown: handleMultiLineKeyDown, handleChange: handleMultiLineChange } = useMultiLineInput({
    onSubmit: () => {
      // Create a synthetic form event and call handleSendMessage
      const syntheticEvent = { preventDefault: () => { } } as FormEvent;
      handleSendMessage(syntheticEvent);
    },
    maxRows: 5,
    minRows: 1
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Initialize chatService with database support on component mount
  useEffect(() => {
    if (initializationAttemptedRef.current) {
      return;
    }
    initializationAttemptedRef.current = true;

    const initializeChatService = async () => {
      setIsLoading(true);
      try {
        await chatService.initialize();
        const storedMessages = getChatMessagesSnapshot();
        setMessages(storedMessages);
        const activeSessionId = chatService.getCurrentSessionId();
        setCurrentSessionId(activeSessionId);

        // Set event bus for chatService
        chatService.setEventBus(eventBus);

        if (!initialSessionId && activeSessionId) {
          onSessionChange?.(activeSessionId);
        }

        // Scroll to bottom after messages are loaded
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        console.error('Failed to initialize chat service:', error);
        setError('Failed to load chat session. Please try refreshing the page.');
        // Fallback to stored messages
        const storedMessages = getChatMessagesSnapshot();
        setMessages(storedMessages);
        chatService.setEventBus(eventBus);
      } finally {
        setIsLoading(false);
        setIsServiceInitialized(true);
      }
    };

    initializeChatService();
  }, [eventBus, initialSessionId, onSessionChange, scrollToBottom]);

  useEffect(() => {
    const handleSessionUpdated = () => {
      triggerSessionRefresh();
    };

    eventBus.subscribe(EVENTS.CHAT_SESSION_UPDATED, handleSessionUpdated);

    return () => {
      eventBus.unsubscribe(EVENTS.CHAT_SESSION_UPDATED, handleSessionUpdated);
    };
  }, [eventBus, triggerSessionRefresh]);

  useEffect(() => {
    const handleChatOpen = async (data: string | ChatOpenEventData) => {
      const { prompt, createNewSession } = typeof data === 'string'
        ? { prompt: data, createNewSession: false }
        : {
          prompt: data?.prompt,
          createNewSession: data?.createNewSession ?? false
        };

      if (
        !prompt ||
        isLoading ||
        historyReconciliationRequired ||
        hasPendingHumanInput ||
        requestInProgressRef.current
      ) {
        return; // Prevent duplicate calls when already loading
      }

      setIsLoading(true);
      setStreamingContent('');
      setError(null);
      requestInProgressRef.current = true;
      let request: ChatRequestHandle | null = null;

      try {
        if (createNewSession) {
          await createAndActivateSession();
        } else {
          // Clear chat history first when opening from event
          chatService.saveMessages([]);
          setMessages([]);
        }

        request = chatService.sendMessage(
          prompt,
          (chunk) => {
            setStreamingContent(prev => prev + chunk);
            // Update messages during streaming to catch SSE events
            const currentMessages = getChatMessagesSnapshot();
            setMessages(currentMessages);
          }
        );
        registerChatRequest(request);
        await request;

        // Update messages from chatService after response
        const updatedMessages = getChatMessagesSnapshot();
        // Set all messages including UI-only server progress messages
        setMessages(updatedMessages);
        setStreamingContent('');

        // Scroll to bottom after response is complete - use setTimeout for reliable DOM update
        setTimeout(() => scrollToBottom(), 0);

      } catch (error) {
        const errorMessage = getErrorMessage(error);
        console.error('❌ Chat error:', errorMessage);
        setError(errorMessage);
      } finally {
        if (request) {
          clearChatRequest(request);
        }
        setIsLoading(false);
        setStreamingContent('');
        requestInProgressRef.current = false;
      }
    };

    eventBus.subscribe(EVENTS.CHAT_OPEN, handleChatOpen);

    return () => {
      eventBus.unsubscribe(EVENTS.CHAT_OPEN, handleChatOpen);
    };
  }, [
    eventBus,
    isLoading,
    historyReconciliationRequired,
    hasPendingHumanInput,
    createAndActivateSession,
    registerChatRequest,
    clearChatRequest,
    scrollToBottom
  ]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(BREAKPOINTS.desktop);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    if (!isDesktopLayout) {
      setSidebarCollapsed(true);
    }
  }, [isDesktopLayout]);


  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();

    if (
      !validateFormInput(inputValue) ||
      isLoading ||
      historyReconciliationRequired ||
      requestInProgressRef.current
    ) return;

    if (pendingHumanInputMessage) {
      if (!composerHumanInputQuestion) {
        return;
      }
      const freeformAnswer = normalizeHumanInputFreeform(inputValue);
      if (!freeformAnswer) {
        return;
      }
      setInputValue('');
      const submitted = await handleHumanInputSubmit(pendingHumanInputMessage.id, {
        status: 'answered',
        answers: {
          [composerHumanInputQuestion.id]: freeformAnswer
        }
      });
      if (!submitted) {
        setInputValue(freeformAnswer);
      }
      return;
    }

    const messageContent = inputValue.trim();

    // Immediately clear input and set loading to prevent double submission
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');
    setError(null);
    requestInProgressRef.current = true;
    let request: ChatRequestHandle | null = null;
    try {
      const sendPromise = chatService.sendMessage(
        messageContent,
        (chunk) => {
          setStreamingContent(prev => prev + chunk);
          // Update messages during streaming to catch SSE events
          const currentMessages = getChatMessagesSnapshot();
          setMessages(currentMessages);
        }
      );
      request = sendPromise;
      registerChatRequest(request);

      // Get updated messages immediately after sendMessage starts (user message is added synchronously)
      const updatedMessagesAfterUserMessage = getChatMessagesSnapshot();
      setMessages(updatedMessagesAfterUserMessage);

      // Scroll to bottom immediately after user message is added - use setTimeout for reliable DOM update
      setTimeout(() => scrollToBottom(), 0);

      // Wait for the full response
      await sendPromise;

      // Update messages from chatService after response
      const finalUpdatedMessages = getChatMessagesSnapshot();
      // Set all messages including UI-only server progress messages
      setMessages(finalUpdatedMessages);
      setStreamingContent('');

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('❌ Message send error (handleSendMessage):', {
        error: errorMessage,
        errorObject: error,
        errorStack: error instanceof Error ? error.stack : undefined,
        errorType: error?.constructor?.name,
        messageContent: messageContent,
        currentMessages: chatService.getMessages(),
        chatServiceState: {
          messageCount: chatService.getMessages().length,
          lastMessage: chatService.getMessages().slice(-1)[0]
        },
        requestState: {
          isLoading,
          requestInProgress: requestInProgressRef.current,
          streamingContent: streamingContent.length
        }
      });
      setError(errorMessage);
      setStreamingContent('');

      if (isSessionExpiredError(error)) {
        setTimeout(() => setError(null), 5000);
      }
    } finally {
      if (request) {
        clearChatRequest(request);
      }
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  };

  // Helper function for UserChoice to submit messages directly
  const handleSubmitFromChoice = async (message: string) => {
    if (
      isLoading ||
      historyReconciliationRequired ||
      hasPendingHumanInput ||
      requestInProgressRef.current
    ) return;

    setIsLoading(true);
    setStreamingContent('');
    setError(null);
    requestInProgressRef.current = true;
    let request: ChatRequestHandle | null = null;

    try {
      const sendPromise = chatService.sendMessage(
        message,
        (chunk) => {
          setStreamingContent(prev => prev + chunk);
          // Update messages during streaming to catch SSE events
          const currentMessages = getChatMessagesSnapshot();
          setMessages(currentMessages);
        }
      );
      request = sendPromise;
      registerChatRequest(request);

      // Get updated messages immediately after sendMessage starts
      const updatedMessagesAfterUserMessage = getChatMessagesSnapshot();
      setMessages(updatedMessagesAfterUserMessage);

      // Scroll to bottom immediately after user message is added - use setTimeout for reliable DOM update
      setTimeout(() => scrollToBottom(), 0);

      // Wait for the full response
      await sendPromise;

      // Update messages from chatService after response
      const finalUpdatedMessages = getChatMessagesSnapshot();
      setMessages(finalUpdatedMessages);
      setStreamingContent('');

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('❌ Message send error (handleSubmitFromChoice):', {
        error: errorMessage,
        errorObject: error,
        errorStack: error instanceof Error ? error.stack : undefined,
        errorType: error?.constructor?.name,
        choiceMessage: message,
        currentMessages: chatService.getMessages(),
        chatServiceState: {
          messageCount: chatService.getMessages().length,
          lastMessage: chatService.getMessages().slice(-1)[0]
        },
        requestState: {
          isLoading,
          requestInProgress: requestInProgressRef.current,
          streamingContent: streamingContent.length
        }
      });
      setError(errorMessage);
      setStreamingContent('');

      if (isSessionExpiredError(error)) {
        setTimeout(() => setError(null), 5000);
      }
    } finally {
      if (request) {
        clearChatRequest(request);
      }
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  };

  const handleHumanInputSubmit = async (messageId: number, answer: HumanInputAnswer) => {
    if (isLoading || historyReconciliationRequired || requestInProgressRef.current) return false;

    setIsLoading(true);
    setStreamingContent('');
    setError(null);
    requestInProgressRef.current = true;
    let request: ChatRequestHandle | null = null;

    try {
      const sendPromise = chatService.submitHumanInput(
        messageId,
        answer,
        (chunk) => {
          setStreamingContent(prev => prev + chunk);
          setMessages(getChatMessagesSnapshot());
        }
      );
      request = sendPromise;
      registerChatRequest(request);

      setMessages(getChatMessagesSnapshot());
      setTimeout(() => scrollToBottom(), 0);

      await sendPromise;

      setMessages(getChatMessagesSnapshot());
      setStreamingContent('');
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('❌ Human input submit error:', {
        error: errorMessage,
        errorObject: error,
        errorStack: error instanceof Error ? error.stack : undefined,
        messageId,
        answer
      });
      setError(errorMessage);
      setStreamingContent('');
      return false;
    } finally {
      if (request) {
        clearChatRequest(request);
      }
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  };

  // Handle message editing
  const handleEditMessage = async (messageId: number, newContent: string) => {
    if (isLoading || historyReconciliationRequired || requestInProgressRef.current) return;

    setIsLoading(true);
    setIsEditPersisting(true);
    setStreamingContent('');
    setError(null);
    requestInProgressRef.current = true;
    let request: ChatRequestHandle | null = null;

    try {
      // Edit the message in chatService (updates content in frontend and database, truncates history)
      await chatService.editMessage(messageId, newContent);

      // Get updated messages after edit
      const updatedMessages = getChatMessagesSnapshot();
      flushSync(() => {
        setMessages(updatedMessages);
        setIsEditPersisting(false);
      });

      // Resubmit the conversation to LLM using the resubmitAfterEdit method
      const sendPromise = chatService.resubmitAfterEdit(
        (chunk) => {
          setStreamingContent(prev => prev + chunk);
          // Update messages during streaming to catch SSE events
          const currentMessages = getChatMessagesSnapshot();
          setMessages(currentMessages);
        }
      );
      request = sendPromise;
      registerChatRequest(request);

      // Scroll to bottom immediately after edit
      setTimeout(() => scrollToBottom(), 0);

      // Wait for the full response
      await sendPromise;

      // Update messages from chatService after response
      const finalUpdatedMessages = getChatMessagesSnapshot();
      setMessages(finalUpdatedMessages);
      setStreamingContent('');

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setMessages(getChatMessagesSnapshot());
      console.error('❌ Edit message error:', {
        error: errorMessage,
        errorObject: error,
        errorStack: error instanceof Error ? error.stack : undefined,
        errorType: error?.constructor?.name,
        messageId,
        newContent,
        currentMessages: chatService.getMessages(),
        chatServiceState: {
          messageCount: chatService.getMessages().length,
          lastMessage: chatService.getMessages().slice(-1)[0]
        }
      });
      setError(errorMessage);
      setStreamingContent('');

      if (isSessionExpiredError(error)) {
        // Handle session expiry if needed
      }
    } finally {
      setIsEditPersisting(false);
      if (request) {
        clearChatRequest(request);
      }
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  };

  // Handle saving a message as a prompt
  const handleSaveAsPrompt = async (content: string) => {
    try {
      await promptService.createPrompt(content);
      // Trigger sidebar refresh by publishing event
      if (eventBus) {
        eventBus.publish(EVENTS.PROMPTS_REFRESH, { showTab: true });
      }
    } catch (error) {
      console.error('Failed to save prompt:', error);
    }
  };

  // Handle copying all messages to clipboard
  const handleCopyMessages = async () => {

    try {
      // Check if there are messages to copy
      if (getCopiableMessageCount(messages) === 0) {
        console.warn('No messages to copy');
        return;
      }

      // Format messages for clipboard using utility
      const formattedMessages = formatMessagesForClipboard(messages);

      if (!formattedMessages) {
        console.warn('No messages to copy');
        return;
      }

      await navigator.clipboard.writeText(formattedMessages);

      // Show "Copied" state for 3 seconds
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to copy messages:', error);

      // Fallback for older browsers
      try {
        const formattedMessages = formatMessagesForClipboard(messages);

        if (!formattedMessages) {
          console.warn('No messages to copy (fallback)');
          return;
        }

        const textArea = document.createElement('textarea');
        textArea.value = formattedMessages;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        // Show "Copied" state for 3 seconds
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 3000);
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
      }
    }
  };

  // Voice recognition setup
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);

  useEffect(() => {
    // Initialize speech recognition using utility

    if (!isSpeechRecognitionSupported()) {
      console.warn('Speech recognition not supported in this browser.');
      return;
    }

    const recognitionInstance = createSpeechRecognition(
      {
        continuous: false,
        interimResults: false,
        lang: 'en-US'
      },
      {
        onResult: (transcript: string) => {
          setInputValue(transcript);
          setIsListening(false);
        },
        onError: (event: Event) => {
          console.error('Speech recognition error:', event);
          setIsListening(false);
        }
      }
    );

    setRecognition(recognitionInstance);

    return () => {
      cleanupSpeechRecognition(recognitionInstance);
    };
  }, []);

  const handleToggleListening = () => {
    if (isListening) {
      recognition?.stop();
    } else {
      setInputValue(''); // Clear input when starting new recognition
      recognition?.start();
    }
    setIsListening(prev => !prev);
  };

  // Drop zone handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Only set isDragOver to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    try {
      // Extract note data from drag transfer
      const dragDataString = e.dataTransfer.getData('application/json');
      if (!dragDataString) {
        return;
      }

      const noteData: NoteDragToChatEventData = JSON.parse(dragDataString);

      // Convert drag data back to Note object format for summaryUtils
      const note = {
        id: noteData.noteId,
        title: noteData.title,
        content: noteData.content,
        author: noteData.author,
        type: noteData.type,
        accountName: noteData.accountName,
        contactName: noteData.contactName,
        updatedAt: new Date(noteData.updatedAt),
        // Add other required Note properties with defaults
        createdAt: new Date(noteData.updatedAt),
        createdBy: 0,
        updatedBy: 0,
        teamId: 0,
        teamIds: '',
        accountId: undefined,
        contactId: undefined,
        contactid: undefined,
        objectIds: '',
        objectType: (noteData.accountName ? 'account' : noteData.contactName ? 'contact' : 'account') as 'account' | 'contact',
        flags: 0
      };

      // Use the existing summary utility (same as AccountNotes uses)
      const prompt = getNotesSummaryPrompt([note]);

      // Use the existing chat open event to send the formatted prompt (same as AccountNotes)
      const chatOpenPayload: ChatOpenEventData = {
        prompt,
        createNewSession: true,
      };
      publish(EVENTS.CHAT_OPEN, chatOpenPayload);

    } catch (error) {
      console.error('❌ Error processing dropped note:', error);
      setError('Failed to process the dropped note. Please try again.');
    }
  };

  // Event bus subscription for chat opening for drop
  useEffect(() => {
    const handleChatOpenForDrop = () => {
      // Chat is already open when this component is rendered
      // This event is mainly to signal that drag is happening
    };

    eventBus.subscribe(EVENTS.CHAT_OPEN_FOR_DROP, handleChatOpenForDrop);

    return () => {
      eventBus.unsubscribe(EVENTS.CHAT_OPEN_FOR_DROP, handleChatOpenForDrop);
    };
  }, [eventBus]);

  // Session management handlers
  const handleSessionSelect = useCallback(async (
    sessionId: number,
    options?: { recoverToActiveSession?: boolean }
  ) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      if (options?.recoverToActiveSession) {
        await chatSessionService.getSession(sessionId);
      }

      await chatService.switchSession(sessionId);
      const newMessages = getChatMessagesSnapshot();
      setMessages(newMessages);
      setCurrentSessionId(sessionId);
      setStreamingContent('');
      setError(null);
      onSessionChange?.(sessionId);

      // Scroll to bottom after messages are loaded
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      const activeSessionId = chatService.getCurrentSessionId();
      if (
        options?.recoverToActiveSession &&
        activeSessionId &&
        activeSessionId !== sessionId &&
        isUnavailableChatSessionError(error, sessionId)
      ) {
        setMessages(getChatMessagesSnapshot());
        setCurrentSessionId(activeSessionId);
        setStreamingContent('');
        setError(null);
        onSessionChange?.(activeSessionId, { replace: true });
        return;
      }

      console.error('Failed to switch session:', error);
      setError('Failed to load session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, onSessionChange, scrollToBottom]);

  const handleNewSession = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await createAndActivateSession();

      // Focus input field for immediate typing
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (error) {
      console.error('Failed to create new session:', error);
      setError('Failed to create new session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptSelect = (promptContent: string) => {
    setInputValue(promptContent);
    // Auto-focus the input field
    textareaRef.current?.focus();
  };

  useEffect(() => {
    if (!isServiceInitialized) return;

    if (!initialSessionId) {
      initialSessionAttemptRef.current = null;
      return;
    }

    if (currentSessionId === initialSessionId) {
      initialSessionAttemptRef.current = initialSessionId;
      return;
    }

    if (initialSessionAttemptRef.current === initialSessionId) {
      return;
    }

    initialSessionAttemptRef.current = initialSessionId;
    handleSessionSelect(initialSessionId, { recoverToActiveSession: true });
  }, [initialSessionId, currentSessionId, isServiceInitialized, handleSessionSelect]);

  const showStarterState = !isLoading && messages.length === 0 && !error;
  const starterIsCompact = !isFullPage;

  return (
    <div className="flex h-full min-w-0 max-w-full">
      {/* Chat Sidebar */}
      {showSessionSidebar && (
        <div className={`${sidebarCollapsed ? 'w-12' : 'w-80'} flex-none border-r border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300`}>
          <ChatSidebar
            activeSessionId={currentSessionId || undefined}
            onSessionSelect={handleSessionSelect}
            onPromptSelect={handlePromptSelect}
            isLoading={isLoading}
            collapsed={sidebarCollapsed}
            onCollapsedChange={handleSidebarCollapsedChange}
            refreshToken={sessionRefreshToken}
          />
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex min-w-0 max-w-full flex-1 justify-center">
        <div
          className={`${isFullPage ? 'max-w-6xl' : 'max-w-4xl'} relative flex h-full min-w-0 w-full max-w-full flex-col bg-white shadow-lg dark:bg-gray-800 ${isDragOver
            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-900/20'
            : ''
            } transition-all duration-200`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drop zone overlay */}
          {isDragOver && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 border-2 border-dashed border-blue-500 rounded-lg">
              <div className="text-center p-6">
                <svg className="w-12 h-12 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                <p className="text-lg font-medium text-blue-700 dark:text-blue-300 mb-2">Drop note here to summarize</p>
                <p className="text-sm text-blue-600 dark:text-blue-400">AI will analyze and summarize the note content</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex-none bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-6">
              <div className="flex min-w-0 items-center gap-2">
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-200" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v4.7c0 4.83-3.4 9.36-7 10.6-3.6-1.24-7-5.77-7-10.6V6.3l7-3.12z" fill="currentColor" />
                </svg>
                <span className="text-lg font-medium text-gray-900 dark:text-white">Chat</span>
              </div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                <button
                  onClick={handleCopyMessages}
                  disabled={getCopiableMessageCount(messages) === 0 || isCopied}
                  className="chat-button-hover px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title={isCopied ? "Messages copied!" : "Copy all messages to clipboard"}
                >
                  {isCopied ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                  {isCopied ? 'Copied' : ''}
                </button>
                <button
                  onClick={handleNewSession}
                  className="chat-button-hover px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Chat
                </button>
                {onOpenFullPage && (
                  <button
                    onClick={onOpenFullPage}
                    className="text-gray-500 hover:text-gray-900 dark:hover:text-white chat-ease-in-out-back transition-all duration-300 transform hover:scale-110 active:scale-95"
                    title="Open full-page chat"
                  >
                    <svg className="w-5 h-5 transition-transform duration-300 ease-in-out" fill="currentColor" viewBox="0 0 512 512">
                      <g transform="translate(85.333333, 64.000000)">
                        <path d="M128,63.999444 L128,106.666444 L42.6666667,106.666667 L42.6666667,320 L256,320 L256,234.666444 L298.666,234.666444 L298.666667,362.666667 L4.26325641e-14,362.666667 L4.26325641e-14,64 L128,63.999444 Z M362.666667,1.42108547e-14 L362.666667,170.666667 L320,170.666667 L320,72.835 L143.084945,249.751611 L112.915055,219.581722 L289.83,42.666 L192,42.6666667 L192,1.42108547e-14 L362.666667,1.42108547e-14 Z" id="Combined-Shape" />
                      </g>
                    </svg>
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-white chat-ease-in-out-back transition-all duration-300 transform hover:scale-110 active:scale-95 hover:rotate-90"
                  >
                    <svg className="w-5 h-5 transition-transform duration-300 ease-in-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Loading state for initial load */}
            {isLoading && messages.length === 0 && !error && (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="flex space-x-2 mb-4">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <p className="text-gray-600 dark:text-gray-400">Loading conversation...</p>
              </div>
            )}

            {/* Empty state for new/empty sessions */}
            {showStarterState && (
              <div className={`flex min-h-full flex-col items-center ${starterIsCompact ? 'px-3 py-3' : 'px-4 py-8 sm:px-6 lg:px-8'}`}>
                <div className={`${starterIsCompact ? 'mb-3' : 'mb-6'} flex flex-col items-center text-center`}>
                  <div className={`relative ${starterIsCompact ? 'mb-2' : 'mb-4'}`}>
                    <div className={`flex items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300 ${starterIsCompact ? 'h-12 w-12' : 'h-16 w-16'}`}>
                      <ChatBubbleLeftRightIcon className={starterIsCompact ? 'h-7 w-7' : 'h-9 w-9'} aria-hidden="true" />
                    </div>
                    <SparklesIcon className={`absolute text-blue-400 dark:text-blue-300 ${starterIsCompact ? '-right-2 -top-1 h-5 w-5' : '-right-3 -top-2 h-6 w-6'}`} aria-hidden="true" />
                  </div>
                  <h2 className={`font-semibold text-gray-950 dark:text-gray-50 ${starterIsCompact ? 'text-lg leading-6' : 'text-2xl'}`}>
                    Ask AI about your business actions
                  </h2>
                  <p className={`mt-2 max-w-3xl text-gray-600 dark:text-gray-300 ${starterIsCompact ? 'text-xs leading-5' : 'text-sm leading-6'}`}>
                    {starterIsCompact
                      ? 'Your assistant can help you find risks, discover opportunities, and recommend next actions.'
                      : 'Find risks, opportunities, and next actions across Power BI, CRM notes, account history, and app data.'}
                  </p>
                  {!starterIsCompact && (
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                      Start with a question or choose an example below.
                    </p>
                  )}
                </div>

                <div className={`w-full max-w-6xl ${starterIsCompact ? 'flex flex-col gap-2' : 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,17rem),1fr))] gap-3'}`}>
                  {starterCategories.map(({ title, tone, Icon, prompts }) => {
                    const toneClasses = starterToneClasses[tone];

                    return (
                      <section
                        key={title}
                        className={`rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${starterIsCompact ? 'p-2' : 'p-3'}`}
                        aria-labelledby={`starter-${tone}`}
                      >
                        <div className={`flex items-center gap-3 ${starterIsCompact ? 'mb-1.5' : 'mb-3'}`}>
                          <span className={`flex shrink-0 items-center justify-center rounded-lg ${toneClasses.iconWrap} ${starterIsCompact ? 'h-8 w-8' : 'h-10 w-10'}`}>
                            <Icon className={`${starterIsCompact ? 'h-5 w-5' : 'h-6 w-6'} ${toneClasses.icon}`} aria-hidden="true" />
                          </span>
                          <h3 id={`starter-${tone}`} className={`font-semibold text-gray-950 dark:text-gray-50 ${starterIsCompact ? 'text-sm' : 'text-sm'}`}>
                            {title}
                          </h3>
                        </div>
                        <div className={starterIsCompact ? 'flex flex-wrap gap-1.5' : 'space-y-2'}>
                          {prompts.map((prompt) => (
                            <button
                              key={prompt.label}
                              type="button"
                              onClick={() => handlePromptSelect(prompt.prompt)}
                              className={`group flex max-w-full items-center gap-2 border border-gray-200 bg-white text-left text-xs font-medium text-gray-700 transition-colors hover:border-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-200 dark:hover:border-blue-900 dark:hover:bg-blue-950/30 ${starterIsCompact ? 'min-h-6 rounded-full px-2.5 py-0.5 leading-4' : 'min-h-11 w-full justify-between rounded-lg px-3 py-2 leading-5'}`}
                            >
                              <span className={starterIsCompact ? 'truncate' : undefined}>
                                {starterIsCompact ? prompt.compactLabel : prompt.label}
                              </span>
                              <ChevronRightIcon className={`${starterIsCompact ? 'hidden' : 'block'} h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 dark:text-gray-500`} aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Messages */}
            {(messages.length > 0 || error) && (
              <div className="px-6 space-y-4">
                {(() => {
                  const sortedMessages = sortMessagesByTimestamp(messages);
                  const visibleMessages = displayMessages(sortedMessages);

                  return visibleMessages.map((message: ChatMessage, index) => {
                    try {
                      const previousMessage = visibleMessages[index - 1];
                      const isToolProgressMessage = message.role === 'sse_event' && message.isUIOnly && message.sseEventType !== 'human_input';
                      const previousIsToolProgressMessage = previousMessage?.role === 'sse_event' && previousMessage.isUIOnly && previousMessage.sseEventType !== 'human_input';
                      const compactProgressSpacing = isToolProgressMessage && previousIsToolProgressMessage ? '-mt-3' : '';

                      // SSE Event messages - show progress hints immediately and persistently
                      if (message.role === 'sse_event' && message.isUIOnly) {
                        if (message.sseEventType === 'human_input') {
                          const toolCallId = message.eventData?.details?.pendingHumanInput?.toolCallId;
                          if (toolCallId && answeredHumanInputToolCallIds.has(toolCallId)) {
                            return null;
                          }

                          return (
                            <div key={message.id}>
                              <HumanInputMessage
                                message={message}
                                disabled={
                                  isLoading ||
                                  historyReconciliationRequired ||
                                  requestInProgressRef.current
                                }
                                onSubmit={handleHumanInputSubmit}
                              />
                            </div>
                          );
                        }

                        if (message.sseEventType === 'client_action') {
                          return <ClientActionMessage key={message.id} message={message} />;
                        }

                        return (
                          <div key={message.id} className={compactProgressSpacing}>
                            <SSEProgressMessage message={message} />
                          </div>
                        );
                      }

                      // Tool messages - DO NOT DISPLAY per requirement
                      if (message.role === 'tool') {
                        return null;
                      }

                      // Regular user/assistant messages
                      if (message.role === 'user') {
                        return (
                          <UserMessage
                            key={message.id}
                            message={message}
                            onEdit={
                              historyReconciliationRequired || message.isUIOnly
                                ? undefined
                                : handleEditMessage
                            }
                            onSaveAsPrompt={handleSaveAsPrompt}
                          />
                        );
                      } else if (message.role === 'assistant' && message.content?.trim()) {
                        return <AssistantMessage key={message.id} message={message} onSubmitMessage={handleSubmitFromChoice} />;
                      }

                      return null;
                    } catch (error) {
                      console.error('Error rendering message:', error, message);
                      return null; // Skip problematic items
                    }
                  });
                })()}
                {streamingContent && (
                  <AssistantMessage
                    message={{
                      id: -1, // Temporary ID for streaming content
                      role: 'assistant',
                      content: streamingContent,
                      timestamp: new Date().toISOString()
                    }}
                    onSubmitMessage={handleSubmitFromChoice}
                    isStreaming={true}
                  />
                )}
                {isLoading && !isEditPersisting && !streamingContent && (
                  <div className="flex justify-start" role="status" aria-label="Assistant response pending">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="flex justify-center my-4">
                    <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-3">
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="flex-1">{error}</span>
                      <button
                        onClick={() => {
                          setError(null);
                          // Retry initialization if no session loaded
                          if (!currentSessionId) {
                            const initializeChatService = async () => {
                              setIsLoading(true);
                              try {
                                await chatService.initialize();
                                const storedMessages = getChatMessagesSnapshot();
                                setMessages(storedMessages);
                                setCurrentSessionId(chatService.getCurrentSessionId());
                                chatService.setEventBus(eventBus);
                                setTimeout(() => scrollToBottom(), 100);
                              } catch (error) {
                                console.error('Failed to initialize chat service:', error);
                                setError('Failed to load chat session. Please try refreshing the page.');
                              } finally {
                                setIsLoading(false);
                              }
                            };
                            initializeChatService();
                          }
                        }}
                        className="px-3 py-1 bg-red-200 dark:bg-red-800 hover:bg-red-300 dark:hover:bg-red-700 rounded text-sm font-medium transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area - Fixed to bottom */}
          <div className="flex-none bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-5 pt-4">
            {showStarterState && (
              <div className="mb-3">
                <div className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Quick suggestions
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap">
                  {quickSuggestions.map(({ label, prompt, tone, Icon }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => handlePromptSelect(prompt)}
                      className={`inline-flex min-w-fit items-center gap-2 rounded-lg border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${starterIsCompact ? 'min-h-7 px-2.5 py-1 text-xs' : 'min-h-9 px-3 py-2 text-sm'} ${starterToneClasses[tone].chip}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{label}</span>
                      <ChevronRightIcon className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <form onSubmit={handleSendMessage} className="relative flex min-w-0 max-w-full items-center gap-3">
              {/* Microphone button for voice input */}
              <button
                type="button"
                onClick={handleToggleListening}
                className={`p-3 rounded-full transition-all flex items-center justify-center ${isListening ? 'bg-red-600 text-white animate-pulse' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                aria-label={isListening ? 'Stop listening' : 'Start listening'}
                disabled={
                  isLoading ||
                  historyReconciliationRequired ||
                  (hasPendingHumanInput && !canAnswerPendingHumanInputWithComposer)
                }
              >
                {isListening ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 6h4v4H6zm8 0h4v4h-4zM6 14h4v4H6zm8 0h4v4h-4z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zm1.5 9a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 3 0v6zM19 10v1a7 7 0 0 1-14 0v-1a1 1 0 0 1 2 0v1a5 5 0 0 0 10 0v-1a1 1 0 0 1 2 0zM12 20h.01v2H12v-2z" />
                    <path d="M8 22h8v-2H8v2z" />
                  </svg>
                )}
              </button>

              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => handleMultiLineChange(e, (e) => setInputValue(e.target.value))}
                onKeyDown={handleMultiLineKeyDown}
                placeholder={
                  canAnswerPendingHumanInputWithComposer
                    ? 'Choose an option above or type your answer...'
                    : hasPendingHumanInput
                      ? 'Answer the pending question above...'
                      : 'Enter a prompt here...'
                }
                disabled={
                  isLoading ||
                  historyReconciliationRequired ||
                  (hasPendingHumanInput && !canAnswerPendingHumanInputWithComposer)
                }
                maxLength={canAnswerPendingHumanInputWithComposer ? HUMAN_INPUT_FREEFORM_MAX_LENGTH : undefined}
                rows={1}
                className="block min-w-0 max-w-full flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-hidden"
              />
              {activeChatRequest ? (
                <button
                  type="button"
                  onClick={() => handleStopGenerating(activeChatRequest)}
                  aria-label="Stop generating"
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    historyReconciliationRequired ||
                    (hasPendingHumanInput && !canAnswerPendingHumanInputWithComposer)
                  }
                  aria-label="Send message"
                  className="inline-flex items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              )}
            </form>
            <div className="text-xs text-gray-500 dark:text-gray-400 my-2 text-center">
              {activeChatRequest
                ? 'AI is working. Stop at any time.'
                : isEditPersisting
                  ? 'Saving edit...'
                : isLoading
                  ? 'Loading chat...'
                : canAnswerPendingHumanInputWithComposer
                  ? 'Your message will answer the pending question.'
                  : hasPendingHumanInput
                    ? 'Choose an answer from the pending question above.'
                  : 'AI can make mistakes, so double-check it.'}
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Chat;
